/**
 * RegisterProjectModal — the admin "＋ Register project" flow (mockup
 * `#s-register`): a two-step `Dialog` — step 1 registers the project
 * (`POST /projects` via `useRegisterProjectMutation`), step 2 embeds
 * `IngestPoolControl` to load its case pool once the project id is real
 * (an ingest-after-register flow, since `POST /pool` needs a project that
 * already exists server-side).
 *
 * Fields not surfaced in the mockup form (`label_schema`, `instructions_key`)
 * are submitted with placeholder defaults (`{}` / `null`) rather than
 * guessed content — nothing here fabricates clinical/schema data. `seed`
 * (required by `PhaseConfig` but absent from the mockup) gets its own input
 * with a documented default of `1`, since registration is the only place a
 * seed could ever be set — there is no other UI path that assigns one.
 *
 * `created_at` is stamped client-side at submit with `new Date().toISOString()`
 * per the Task 9 brief (this runs in the browser, not a workflow sandbox).
 */

import { useState } from "react";

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@/design-system/components/react";
import { useRegisterProjectMutation } from "../../../../hooks/useFactoryQueries";
import type { Project } from "../../../../types/factory";
import { logger } from "../../../../utils/logger";
import { IngestPoolControl } from "./IngestPoolControl";

export interface RegisterProjectModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  project_id: string;
  task: string;
  name: string;
  demo_total: string;
  demo_unique: string;
  demo_gt: string;
  working_total: string;
  working_unique: string;
  working_overlap: string;
  seed: string;
  auto_advance_from_phase: string;
  gt_annotator: string;
  email_recipients: string;
  workflow: "standard" | "cohort_segmentation";
  source_cohort_id: string;
}

const COHORT_SEGMENTATION_TASK = "renal_stone";

const INITIAL_FORM: FormState = {
  project_id: "",
  task: "segmentation",
  name: "",
  demo_total: "20",
  demo_unique: "15",
  demo_gt: "5",
  working_total: "100",
  working_unique: "90",
  working_overlap: "10",
  seed: "1",
  auto_advance_from_phase: "2",
  gt_annotator: "",
  email_recipients: "",
  workflow: "standard",
  source_cohort_id: "",
};

function parseEmailRecipients(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function toInt(raw: string, fallback: number): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * A batch-shape / seed / auto-advance field is valid if it's either empty
 * (falls back to the documented default `toInt` applies) or a whole number.
 * Anything else non-empty (e.g. "abc", "12abc") must block submit instead of
 * `toInt` silently swallowing it into the fallback default — workspace
 * no-silent-fallback rule.
 */
function isValidIntInput(raw: string): boolean {
  const trimmed = raw.trim();
  return trimmed === "" || /^-?\d+$/.test(trimmed);
}

const INT_FIELD_KEYS: (keyof FormState)[] = [
  "demo_total",
  "demo_unique",
  "demo_gt",
  "working_total",
  "working_unique",
  "working_overlap",
  "seed",
  "auto_advance_from_phase",
];

function IntFieldError({ value }: { value: string }) {
  if (isValidIntInput(value)) return null;
  return (
    <span className="text-[11px] text-destructive">
      Must be a whole number — leave blank to use the default.
    </span>
  );
}

export function RegisterProjectModal({ open, onClose }: RegisterProjectModalProps) {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [registeredProjectId, setRegisteredProjectId] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const registerMutation = useRegisterProjectMutation();

  const setField = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const resetAndClose = () => {
    setForm(INITIAL_FORM);
    setRegisteredProjectId(null);
    setRegisterError(null);
    onClose();
  };

  const isValid =
    form.project_id.trim() !== "" &&
    form.task.trim() !== "" &&
    form.name.trim() !== "" &&
    INT_FIELD_KEYS.every((key) => isValidIntInput(form[key])) &&
    (form.workflow === "standard" || form.source_cohort_id.trim() !== "");

  const handleSubmit = () => {
    if (!isValid) return;
    setRegisterError(null);

    const project: Project = {
      project_id: form.project_id.trim(),
      task: form.workflow === "cohort_segmentation" ? COHORT_SEGMENTATION_TASK : form.task.trim(),
      name: form.name.trim(),
      // Not part of the mockup form — no UI collects clinical label schema
      // or an instructions doc key yet, so these are honest empty defaults,
      // never guessed content.
      label_schema: {},
      instructions_key: null,
      workflow: form.workflow,
      source_cohort_id:
        form.workflow === "cohort_segmentation" ? form.source_cohort_id.trim() : null,
      phase_config: {
        demo_total: toInt(form.demo_total, 20),
        demo_unique: toInt(form.demo_unique, 15),
        demo_gt: toInt(form.demo_gt, 5),
        working_total: toInt(form.working_total, 100),
        working_unique: toInt(form.working_unique, 90),
        working_overlap: toInt(form.working_overlap, 10),
        phase2_total: 100,
        seed: toInt(form.seed, 1),
      },
      gate_policy: { auto_advance_from_phase: toInt(form.auto_advance_from_phase, 2) },
      created_at: new Date().toISOString(),
      gt_annotator: form.gt_annotator.trim() || null,
      email_recipients: parseEmailRecipients(form.email_recipients),
    };

    registerMutation.mutate(project, {
      onSuccess: () => setRegisteredProjectId(project.project_id),
      onError: (err: unknown) => {
        logger.error("[RegisterProjectModal] register failed", err);
        setRegisterError(
          "Failed to register the project — check the project id isn't already in use and retry.",
        );
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && resetAndClose()}>
      <DialogContent className="max-w-xl bg-surface border-border-strong">
        <DialogHeader>
          <DialogTitle className="text-fg">
            {registeredProjectId ? "Load case pool" : "New project"}
          </DialogTitle>
        </DialogHeader>

        {!registeredProjectId ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rp-project-id">Project ID</Label>
                <Input
                  id="rp-project-id"
                  className="font-mono"
                  placeholder="colon3d"
                  value={form.project_id}
                  onChange={setField("project_id")}
                />
                <span className="text-[11px] text-fg-faint">
                  Lowercase, no spaces. Used in worklist keys.
                </span>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rp-task">Task</Label>
                <Input
                  id="rp-task"
                  value={form.task}
                  onChange={setField("task")}
                  disabled={form.workflow === "cohort_segmentation"}
                />
                {form.workflow === "cohort_segmentation" && (
                  <span className="text-[11px] text-fg-faint">
                    Reader-cohort segmentation is fixed to urinary stones.
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rp-name">Display name</Label>
              <Input
                id="rp-name"
                placeholder="Colon polyp 3D"
                value={form.name}
                onChange={setField("name")}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-border/50 pt-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rp-workflow">Case source</Label>
                <select
                  id="rp-workflow"
                  value={form.workflow}
                  onChange={(event) => {
                    const workflow = event.target.value as FormState["workflow"];
                    setForm((previous) => ({
                      ...previous,
                      workflow,
                      task:
                        workflow === "cohort_segmentation"
                          ? COHORT_SEGMENTATION_TASK
                          : previous.task,
                    }));
                  }}
                  className="h-9 rounded border border-border-strong bg-surface-2 px-3 text-sm text-fg"
                >
                  <option value="standard">Uploaded cohort file</option>
                  <option value="cohort_segmentation">Reader cohort + AI predictions</option>
                </select>
              </div>
              {form.workflow === "cohort_segmentation" && (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-source-cohort">Reader cohort ID</Label>
                  <Input
                    id="rp-source-cohort"
                    className="font-mono"
                    placeholder="reg-ctsuite-kidney-stone-…"
                    value={form.source_cohort_id}
                    onChange={setField("source_cohort_id")}
                  />
                  <span className="text-[11px] text-fg-faint">
                    The backend pulls canonical cases and prediction geometry directly.
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-border/50 pt-4">
              <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.16em] text-fg-dim">
                Fixed factory phases · Phase 2 = 100 unique
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-demo-total">Demo total</Label>
                  <Input
                    id="rp-demo-total"
                    className="font-mono"
                    value={form.demo_total}
                    onChange={setField("demo_total")}
                    disabled
                  />
                  <IntFieldError value={form.demo_total} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-demo-unique">Demo unique</Label>
                  <Input
                    id="rp-demo-unique"
                    className="font-mono"
                    value={form.demo_unique}
                    onChange={setField("demo_unique")}
                    disabled
                  />
                  <IntFieldError value={form.demo_unique} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-demo-gt">Demo GT</Label>
                  <Input
                    id="rp-demo-gt"
                    className="font-mono"
                    value={form.demo_gt}
                    onChange={setField("demo_gt")}
                    disabled
                  />
                  <IntFieldError value={form.demo_gt} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-working-total">Working total</Label>
                  <Input
                    id="rp-working-total"
                    className="font-mono"
                    value={form.working_total}
                    onChange={setField("working_total")}
                    disabled
                  />
                  <IntFieldError value={form.working_total} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-working-unique">Working unique</Label>
                  <Input
                    id="rp-working-unique"
                    className="font-mono"
                    value={form.working_unique}
                    onChange={setField("working_unique")}
                    disabled
                  />
                  <IntFieldError value={form.working_unique} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-working-overlap">Working overlap</Label>
                  <Input
                    id="rp-working-overlap"
                    className="font-mono"
                    value={form.working_overlap}
                    onChange={setField("working_overlap")}
                    disabled
                  />
                  <IntFieldError value={form.working_overlap} />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3 border-t border-border/50 pt-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rp-seed">Seed</Label>
                <Input
                  id="rp-seed"
                  className="w-20 font-mono"
                  value={form.seed}
                  onChange={setField("seed")}
                />
                <IntFieldError value={form.seed} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rp-auto-advance">Auto-advance from phase</Label>
                <Input
                  id="rp-auto-advance"
                  className="w-24 font-mono"
                  value={form.auto_advance_from_phase}
                  onChange={setField("auto_advance_from_phase")}
                />
                <IntFieldError value={form.auto_advance_from_phase} />
              </div>
              <span className="max-w-[220px] pb-2 text-[11px] text-fg-faint">
                Phases below this need your approval; at/after it, annotators advance on their own.
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-border/50 pt-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rp-gt-annotator">Golden-GT annotator</Label>
                <Input
                  id="rp-gt-annotator"
                  className="font-mono"
                  placeholder="rad.gt@a2zradiology.ai"
                  value={form.gt_annotator}
                  onChange={setField("gt_annotator")}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="rp-recipients">Email recipients</Label>
                <Input
                  id="rp-recipients"
                  className="font-mono"
                  placeholder="andrea@a2zradiology.ai"
                  value={form.email_recipients}
                  onChange={setField("email_recipients")}
                />
              </div>
            </div>

            {registerError && (
              <Alert variant="destructive">
                <AlertDescription>{registerError}</AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="text-xs text-fg-muted">
              <b className="font-mono text-fg">{registeredProjectId}</b> registered. Load its case
              pool from a canonicalized cohort file now, or close and ingest later from the project
              detail page.
            </div>
            <IngestPoolControl
              projectId={registeredProjectId}
              workflow={form.workflow}
              sourceCohortId={
                form.workflow === "cohort_segmentation" ? form.source_cohort_id.trim() : null
              }
            />
          </div>
        )}

        <DialogFooter>
          {!registeredProjectId ? (
            <>
              <Button type="button" variant="ghost" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!isValid || registerMutation.isPending}
              >
                {registerMutation.isPending ? "Registering…" : "Register"}
              </Button>
            </>
          ) : (
            <Button type="button" onClick={resetAndClose}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RegisterProjectModal;
